"use client";
import { useEffect, useState } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { logError } from "@jetblue/dotcom-fe-logger";
import {
  getUserConsentDecision,
  whenCurrentConsentStateAvailable,
} from "@jetblue/dotcom-trust-arc";
import type { DynamicYieldProperites } from ".";
import { setPageContext } from ".";
import {
  notifyDynamicYieldScriptError,
  notifyDynamicYieldScriptReady,
  subscribeDynamicYieldConsent,
  updateDynamicYieldConsent,
} from "./consent";
import {
  DEFAULT_DYNAMIC_YIELD_SECTION_ID,
  DYNAMIC_YIELD_CDN_ORIGIN,
} from "./constants";

export const DynamicYieldClient = ({
  sectionId,
  language,
}: DynamicYieldProperites) => {
  const pathname = usePathname();
  const effectiveSectionId = sectionId ?? DEFAULT_DYNAMIC_YIELD_SECTION_ID;
  const [canLoadCoreScripts, setCanLoadCoreScripts] = useState(false);

  useEffect(() => {
    setPageContext({
      currentUrl: globalThis.location.href,
      isHomePage: globalThis.location.pathname === "/",
      language,
    });
  }, [language, pathname]);

  useEffect(() => {
    if (!sectionId) {
      void logError({
        message: "DynamicYieldService initialized without sectionId defined",
      });
    }
  }, [sectionId]);

  useEffect(() => {
    let isMounted = true;
    const unsubscribeFromConsent = subscribeDynamicYieldConsent();

    void whenCurrentConsentStateAvailable().then(() => {
      if (!isMounted) {
        return;
      }

      updateDynamicYieldConsent(getUserConsentDecision()?.functional === true);
      setCanLoadCoreScripts(true);
    });

    return () => {
      isMounted = false;
      unsubscribeFromConsent();
    };
  }, []);

  if (!canLoadCoreScripts) {
    return null;
  }

  return (
    <>
      <Script
        onError={notifyDynamicYieldScriptError}
        onLoad={() => {
          notifyDynamicYieldScriptReady("dynamic");
        }}
        src={`${DYNAMIC_YIELD_CDN_ORIGIN}/api/${effectiveSectionId}/api_dynamic.js`}
      />
      <Script
        onError={notifyDynamicYieldScriptError}
        onLoad={() => {
          notifyDynamicYieldScriptReady("static");
        }}
        src={`${DYNAMIC_YIELD_CDN_ORIGIN}/api/${effectiveSectionId}/api_static.js`}
      />
    </>
  );
};
